import React from "react";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import { getFormattedTime } from "./utils/shared/time";
import { canPlayFromPlayingNext } from "./utils/shared/eventStatus";
import { isLiveOrComing } from "./utils/shared/sortEvents";
import { getThumbnailUrl } from "./utils/shared/images";
import { EVENT_STATUS, SECTION_TYPES, SIDE_PANEL_ACTIONS, SIDE_PANEL_BUTTON_TITLES, UPCOMING_BADGE_LABEL } from "./constants/event";
import Loader from "./components/Loader";
import PregameBadge from "./components/PregameBadge";
import checkScheduleSvg from "../assets/checkSchedule.svg";
import playNowIcon from "../assets/playNowIcon.svg";
import upcomingBroadcastSvg from "../assets/upcomingBroadcast.svg";
import "./epg.scss";

const PlayNowButton = ({ event, cardFocusKey, index, onFocus, onPlayNow, isLoading }) => {
    const { ref, focused } = useFocusable({
        focusKey: `${cardFocusKey}-play`,
        onEnterPress: () => onPlayNow(event),
        onFocus: (focusProps) => {
            onFocus({ ...focusProps, index });
        },
    });

    return (
        <button
            ref={ref}
            type="button"
            className={`playNowButton ${focused ? "playNowButtonFocused" : ""}`}
            onClick={() => onPlayNow(event)}
        >
            {isLoading ? (
                <Loader textColor={focused ? "#000" : "#fafafa"} />
            ) : (
                <>
                    <img src={playNowIcon} alt="" width="32" height="32" />
                    <span>{SIDE_PANEL_BUTTON_TITLES[SIDE_PANEL_ACTIONS.PLAY]}</span>
                </>
            )}
        </button>
    );
};

const EPGPlayingNextCard = React.memo(({ event, onFocus, onEnterPress, onPlayNow, index, rowId, isLoading }) => {
    const { thumbnail, title } = event;
    const focusKey = `${SECTION_TYPES.PLAYING_NEXT}-${rowId}-${index}`;

    const { ref, focused } = useFocusable({
        focusKey,
        onEnterPress,
        onFocus: (focusProps) => {
            onFocus({ ...focusProps, index });
        },
        extraProps: { event, focusKey },
    });

    const mainStartTime = event.information?.main_start_time || event.schedule_start;
    const isLive = isLiveOrComing(event);

    return (
        <div className="playingNextItem">
            <div
                ref={ref}
                role="button"
                tabIndex={0}
                className={`eventCard ${focused ? "focusedCard" : ""}`}
                onClick={() => onEnterPress({ event, focusKey })}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onEnterPress({ event, focusKey }); }}
                style={{ cursor: "pointer" }}
            >
                <div className={`imageContainer ${focused ? "focusedItem" : ""}`}>
                    <img
                        src={getThumbnailUrl(thumbnail)}
                        loading="lazy"
                        decoding="async"
                        className="eventCardImg"
                        alt={title}
                    />
                    <div className="subscribedCheck playingNextCheck">
                        <img src={checkScheduleSvg} alt="Subscribed" width="40" height="40" />
                    </div>
                    <div className="liveContainer">
                        {isLive ? (
                            <div className="liveButton live">{EVENT_STATUS.LIVE}</div>
                        ) : (
                            <div className="liveButton upcomingBadge">
                                <img src={upcomingBroadcastSvg} alt="" />
                                {UPCOMING_BADGE_LABEL}
                            </div>
                        )}
                        {event.schedule_start && <PregameBadge />}
                    </div>
                </div>
                {!isLive && mainStartTime && (
                    <p className="eventCardTime">Event Start&nbsp;{getFormattedTime(mainStartTime)}</p>
                )}
                <p className="eventCardTitle">{title}</p>
                <p className="eventCardLabel">{event.label}</p>
            </div>
            {canPlayFromPlayingNext(event) && (
                <PlayNowButton event={event} cardFocusKey={focusKey} index={index} onFocus={onFocus} onPlayNow={onPlayNow} isLoading={isLoading} />
            )}
        </div>
    );
});

export default EPGPlayingNextCard;
